/**
 * @file Renders the funding source cell for a shadow investor, offering an impersonated Dwolla connect flow.
 */
import { useOutletContext } from "react-router-dom";
import { Space, Tooltip } from "antd";
import { InfoCircleOutlined, WarningOutlined } from "@ant-design/icons";

import { getConfig } from "@components/Config";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { ConnectSourceButton } from "@components/Dwolla";
import { updateInvestmentSourcesOperation } from "@api/services/investments";

import getConnectSourceProps from "./helpers/getConnectSourceProps";
import type { Investment, OutletContext, UpdateInvestment } from "../../../types";

const LABEL_INFO = "Contact [NAME] to link the distributions bank account.";
const LABEL_NO_CONTROLLER = "The organization controller needs to be configured in the settings.";
const LABEL_NOT_AVAILABLE = "Not Available";

const IMPERSONATION_ENABLED_ORG_IDS: string[] = getConfig("IMPERSONATION_ENABLED_ORG_IDS");

type InvestmentOutput = {
  data: Investment;
};

type Props = {
  investment: Investment;
  updateInvestment: UpdateInvestment;
};

/**
 * Renders a connect action for a shadow investor's funding source, or the source name once one is linked.
 */
const ImpersonatedSourceConnect = ({
  investment,
  updateInvestment,
}: Props) => {
  const { request } = useAppContext() as AppContext;

  const {
    getAccount,
    organization,
    computeUserFullname,
    isOrganizationController,
    getOrganizationControllerId,
  } = useOutletContext<OutletContext>();

  const { id, investorAccountId } = investment;

  const {
    receiverSourceId: sourceId,
    receiverSourceName: sourceName,
  } = investment;

  const account = getAccount(investorAccountId);
  const isController = isOrganizationController();
  const hasController = !!getOrganizationControllerId();

  if (!account) {
    return null;
  }

  const { id: organizationId } = organization;
  const isShadowInvestor = !account.correlationId;
  const isImpersonationEnabled = IMPERSONATION_ENABLED_ORG_IDS.includes(organizationId);

  const hasSource = !!sourceId;

  const shouldRenderConnect =
    isShadowInvestor &&
    isController &&
    isImpersonationEnabled;

  if (shouldRenderConnect) {
    /**
     * Sends the selected impersonated source to the backend and updates the investment.
     */
    const onSelect = (receiverSourceId: string) =>
      request<InvestmentOutput>(updateInvestmentSourcesOperation, { id, mutation: { receiverSourceId } })
        .then(({ data }) => updateInvestment(data));

    const connectSourceProps = getConnectSourceProps(investment, onSelect);

    return (
      <ConnectSourceButton {...connectSourceProps} />
    );
  }

  if (!hasSource) {
    const shouldShowTooltip = isShadowInvestor && !isController;

    /**
     * Builds the tooltip title explaining why the funding source is not available.
     */
    const getTitle = () => {
      if (!hasController) {
        return LABEL_NO_CONTROLLER;
      }

      const controllerId = getOrganizationControllerId();
      const controllerName = computeUserFullname(controllerId);

      return LABEL_INFO.replace("[NAME]", controllerName);
    };

    return (
      <Space>
        {LABEL_NOT_AVAILABLE}
        {
          shouldShowTooltip && (
            <Tooltip
              placement="top"
              title={getTitle()}
            >
              {
                hasController ? <InfoCircleOutlined /> : <WarningOutlined />
              }
            </Tooltip>
          )
        }
      </Space>
    );
  }

  return (
    <>{sourceName}</>
  );
};

export default ImpersonatedSourceConnect;
